import { Link } from 'react-router-dom'
import { CardContainer, ChartContainer, ProgressBar } from './styles'
import { TQuestionBook } from './types'
import { Chart as ChartJS, ArcElement } from 'chart.js'
import { Doughnut } from 'react-chartjs-2'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../store/modules/rootReducer'

ChartJS.register(ArcElement)

interface IProps {
  data: TQuestionBook
}

type TLabel = 'correct' | 'wrong' | 'left' | 'correct-percentage'

const Card = (props: IProps) => {
  const { isDarkMode } = useSelector((state: RootState) => state.theme)

  const { hit, wrong } = props.data.statistics

  const [label, setLabel] = useState<TLabel>('correct-percentage')

  const isCompleted = hit.number + wrong.number === props.data.totalQuestions
  const hasStarted = Boolean(hit.number) || Boolean(wrong.number)
  const progress = (() => {
    const value = ((hit.number + wrong.number) / props.data.totalQuestions) * 100

    return value > 1 ? Math.round(value) : Math.round(value * 10) / 10
  })()

  return (
    <CardContainer>
      <div className="start">
        <ChartContainer>
          <div className="label" data-visible={label === 'correct'}>
            <p className="value">{hit.number}</p>
            <p className="text">acertos</p>
          </div>
          <div className="label" data-visible={label === 'wrong'}>
            <p className="value">{wrong.number}</p>
            <p className="text">erros</p>
          </div>
          <div className="label" data-visible={label === 'left'}>
            <p className="value">{props.data.totalQuestions - (hit.number + wrong.number)}</p>
            <p className="text">restantes</p>
          </div>
          <div className="label" data-visible={label === 'correct-percentage'}>
            <p className="value">{Math.round(hit.rate) + '%'}</p>
            <p className="text">{hasStarted ? 'acertos' : '-'}</p>
          </div>

          <Doughnut
            options={{
              onHover(_, activeElement) {
                const labels = {
                  0: 'correct',
                  1: 'wrong',
                }

                setLabel(labels[activeElement[0]?.index] || 'left')
              },
              cutout: 28,
            }}
            onMouseLeave={() => setTimeout(() => setLabel('correct-percentage'), 20)}
            className="chart"
            data={{
              datasets: [
                {
                  data: [
                    props.data.statistics.hit.rate,
                    props.data.statistics.wrong.rate,
                    hasStarted ? 0 : 100,
                  ],
                  backgroundColor: [
                    isDarkMode ? 'rgb(0, 183, 0)' : 'rgb(0, 159, 0)',
                    isDarkMode ? 'rgb(235, 109, 96)' : 'rgb(231, 76, 60)',
                    isDarkMode ? 'rgb(74, 74, 74)' : 'rgb(221, 221, 221)',
                  ],
                  borderColor: isDarkMode ? 'rgb(13, 13, 13)' : 'rgb(255,  255, 255)',
                  hoverBorderColor: isDarkMode ? 'rgb(13, 13, 13)' : 'rgb(255,  255, 255)',
                  borderWidth: 4,
                  hoverBackgroundColor: [
                    isDarkMode ? 'rgb(0, 183, 0, 0.75)' : '#008700',
                    isDarkMode ? 'rgb(235, 109, 96, 0.75)' : '#dc2d1b',
                    isDarkMode ? 'rgb(74, 74, 74, 0.75)' : '#bcbcbc',
                  ],
                },
              ],
            }}
          />
        </ChartContainer>
        <div>
          <p className="name">{props.data.name}</p>

          <div className="infos">
            <span className="info">
              {format(props.data.totalPublicTenders)}{' '}
              {props.data.totalPublicTenders === 1 ? 'órgão' : 'órgãos'} •{' '}
            </span>{' '}
            <span className="info">
              {format(props.data.totalSubjects)}{' '}
              {props.data.totalSubjects === 1 ? 'disciplina' : 'disciplinas'} •{' '}
            </span>{' '}
            <span className="info">
              {format(props.data.totalThemes)}{' '}
              {props.data.totalThemes === 1 ? 'assunto' : 'assuntos'} •{' '}
            </span>{' '}
            <span className="info">
              {format(props.data.totalQuestions)}{' '}
              {props.data.totalQuestions === 1 ? 'questão' : 'questões'}
            </span>
          </div>

          <ProgressBar progress={progress}>
            <div className="text" data-no-progress={progress === 0}>
              {progress}% concluído
            </div>
            <div className="colored-bar">{progress}% concluído</div>
          </ProgressBar>
        </div>
      </div>

      <div className="end">
        <Link className="link" to={`/questoes/caderno/${props.data._id}`}>
          {isCompleted ? 'Ver detalhes' : 'Responder'}
        </Link>
      </div>
    </CardContainer>
  )
}

const format = (num: number) => new Intl.NumberFormat('pt-BR').format(num)

export default Card
